import { Injectable } from '@angular/core';
import { Observable, firstValueFrom } from 'rxjs';
import { AuthService } from '@auth0/auth0-angular';

import { FilesService } from '../services/files.service';
import { ZoomRequest } from '../main/models/request';
import { DisplayType } from '../main/models/file-info';
import { Rectangle } from '@jax-data-science/sci-image-visualizer';
import { TileAccessPort } from '@jax-data-science/sci-image-visualizer';

/**
 * Host-side implementation of the visualization library's {@link TileAccessPort}.
 * Builds the app's concrete `ZoomRequest` from the library-native arguments and
 * the currently-selected file, and hides `DisplayType` behind a method. Bound to
 * the `TILE_ACCESS_PORT` token in `app.module.ts`.
 */
@Injectable({ providedIn: 'root' })
export class TileAccessAdapter implements TileAccessPort {
  constructor(private files: FilesService, private auth: AuthService) {}

  /**
   * Identity for tile requests: the image being RENDERED, not the tree
   * selection (jit-ui#119). The selection moves ahead of the render during an
   * image switch — and also moves for nodes that load no image at all — so
   * keying tiles off it let a tile source built for image A be handed image B's
   * file, mixing one image's pyramid into another's render.
   */
  getSelectedInfoB64(): string | null {
    const file = this.files.getRenderingFileInfo();
    return file ? this.files.encode(file.rawData) : null;
  }

  zoomOnRegion(roi: Rectangle, screen: Rectangle, zIndex: number): Observable<ArrayBuffer> {
    const req = new ZoomRequest();
    req.roi = roi;
    req.screen = screen;
    req.zIndex = zIndex;
    // Same reasoning as getSelectedInfoB64: a zoom must re-crop the image on
    // screen, not whatever happens to be selected in the tree.
    const file = this.files.getRenderingFileInfo();
    if (file) req.info = file.rawData;
    return this.files.zoomOnRegion(req);
  }

  selectDiagramDisplay(): void {
    this.files.selectDisplayType(DisplayType.Diagram);
  }

  async getAuthHeaders(): Promise<Record<string, string>> {
    const token = await firstValueFrom(this.auth.getAccessTokenSilently());
    return token ? { Authorization: `Bearer ${token}` } : {};
  }
}
